import { computerConnectionProgressObservationSchema } from "../../computer-connection-progress.js";
import { array, boolean, discriminatedUnion, enum as enumFactory, int, iso, literal, null as nullFactory, object, record, strictObject, string, union, unknown } from "../../lazy-zod.js";
import { openMeldProfileIdSchema } from "../daemon/profile-identity.js";
import { authProfileKindSchema, authProfileManagedRoleSchema } from "../http/profiles.js";
import { localAgentCatalogSchema } from "../runtime/local-agent-catalog.js";
import { startBackgroundHelperProjectionSchema, startSkillsReadinessProjectionSchema } from "../start/device-projection.js";
//#region src/schemas/prepare/session.ts
const PREPARE_BOUNDED_ID_SCHEMA = string().trim().min(1).max(256);
const prepareSessionContinuationSchema = strictObject({
	prepareSessionId: PREPARE_BOUNDED_ID_SCHEMA,
	localAgentSelection: discriminatedUnion("mode", [
		strictObject({ mode: literal("all") }),
		strictObject({ mode: literal("none") }),
		strictObject({
			mode: literal("specific"),
			localAgentIds: array(PREPARE_BOUNDED_ID_SCHEMA).max(64)
		})
	]).optional()
});
const PREPARE_REASON_CODE_SCHEMA = string().trim().min(1).max(256);
const PREPARE_USER_MESSAGE_SCHEMA = string().trim().min(1).max(512);
const PREPARE_LONG_TEXT_SCHEMA = string().trim().min(1).max(4096);
const PREPARE_STEP_DIAGNOSTICS_MAX_CHARS = 16384;
const PREPARE_STEP_DIAGNOSTICS_SCHEMA = string().trim().min(1).max(PREPARE_STEP_DIAGNOSTICS_MAX_CHARS);
const PREPARE_ISO_DATE_TIME_SCHEMA = iso.datetime({ offset: true }).max(64);
const PREPARE_NULLABLE_DATE_TIME_SCHEMA = union([PREPARE_ISO_DATE_TIME_SCHEMA, nullFactory()]);
const PREPARE_STEP_DIAGNOSTICS_SHORT_TEXT_SCHEMA = string().trim().min(1).max(256);
const PREPARE_STEP_DIAGNOSTICS_LOG_LINE_MAX_CHARS = 321;
const PREPARE_STEP_DIAGNOSTICS_LIFECYCLE_ENTRY_MAX_CHARS = 700;
const PREPARE_STEP_DIAGNOSTICS_COLLECTION_ERROR_MAX_CHARS = 64;
const PREPARE_STEP_DIAGNOSTICS_CLI_VERSION_MAX_CHARS = 64;
const PREPARE_STEP_DIAGNOSTICS_OS_PLATFORM_MAX_CHARS = 64;
const PREPARE_STEP_DIAGNOSTICS_OS_RELEASE_MAX_CHARS = 128;
const PREPARE_STEP_DIAGNOSTICS_SERVICE_JOB_TEXT_MAX_CHARS = 128;
const prepareStepDiagnosticsServiceJobSchema = object({
	controller: enumFactory([
		"launchd",
		"systemd",
		"windows-service",
		"legacy-detached",
		"unknown"
	]),
	loaded: boolean(),
	runs: union([int().nonnegative(), nullFactory()]),
	state: union([string().trim().max(PREPARE_STEP_DIAGNOSTICS_SERVICE_JOB_TEXT_MAX_CHARS), nullFactory()]),
	lastExitCodeText: union([string().trim().max(PREPARE_STEP_DIAGNOSTICS_SERVICE_JOB_TEXT_MAX_CHARS), nullFactory()]),
	lastExitCode: union([int(), nullFactory()]),
	pendedNondemandSpawn: union([string().trim().max(PREPARE_STEP_DIAGNOSTICS_SERVICE_JOB_TEXT_MAX_CHARS), nullFactory()]),
	keepaliveRunAtLoad: boolean()
});
const prepareStepDiagnosticsLifecycleEventTypeSchema = enumFactory([
	"connection.state",
	"daemon.crash",
	"lifecycle.command.begin",
	"lifecycle.command.end",
	"owner.snapshot",
	"route_catalog.initial_load",
	"runtime_state.takeover",
	"stop.controlled_drain",
	"stop.strategy.selected",
	"stop.signal.observed",
	"service_manager.action",
	"service_manager.registration_reconcile",
	"process_stop.action",
	"service_contract.write"
]);
const prepareStepDiagnosticsLifecycleJournalEntrySchema = object({
	schema: literal("openmeld-daemon-lifecycle-event-v1"),
	v: literal(1),
	type: prepareStepDiagnosticsLifecycleEventTypeSchema,
	timestamp: PREPARE_ISO_DATE_TIME_SCHEMA,
	lane: string().trim().min(1).max(64),
	intent: string().trim().min(1).max(64),
	command: string().trim().min(1).max(256),
	pid: int().nonnegative(),
	sessionId: union([PREPARE_STEP_DIAGNOSTICS_SHORT_TEXT_SCHEMA, nullFactory()]),
	result: string().trim().min(1).max(64),
	metadata: record(string().trim().min(1).max(64), unknown()).optional()
}).superRefine((value, ctx) => {
	if (JSON.stringify(value).length > PREPARE_STEP_DIAGNOSTICS_LIFECYCLE_ENTRY_MAX_CHARS) ctx.addIssue({
		code: "custom",
		message: "lifecycle journal entry exceeds diagnostics size budget"
	});
});
const prepareStepDiagnosticsBundleSchema = object({
	bundleVersion: int().positive(),
	collectedAt: PREPARE_ISO_DATE_TIME_SCHEMA,
	supportCode: PREPARE_STEP_DIAGNOSTICS_SHORT_TEXT_SCHEMA,
	nativeFailure: object({ osStatus: int() }).optional(),
	operationFailure: object({
		code: string().regex(/^E[A-Z]+$/u).max(32),
		errno: int().optional(),
		syscall: string().regex(/^[a-z]+$/u).max(32).optional(),
		pathCategory: enumFactory([
			"app_bundle",
			"absolute_other",
			"relative",
			"unknown"
		]),
		destinationCategory: enumFactory([
			"app_bundle",
			"absolute_other",
			"relative",
			"unknown"
		]).optional()
	}).optional(),
	evidenceWindow: object({
		startedAt: PREPARE_ISO_DATE_TIME_SCHEMA,
		endedAt: PREPARE_ISO_DATE_TIME_SCHEMA
	}).optional(),
	serviceLogWindow: object({
		excludedUndatedLines: int().nonnegative(),
		excludedOutsideWindowLines: int().nonnegative(),
		sourceTruncated: boolean()
	}).optional(),
	stepId: PREPARE_STEP_DIAGNOSTICS_SHORT_TEXT_SCHEMA,
	deviceId: PREPARE_STEP_DIAGNOSTICS_SHORT_TEXT_SCHEMA.optional(),
	cliVersion: string().trim().min(1).max(PREPARE_STEP_DIAGNOSTICS_CLI_VERSION_MAX_CHARS),
	os: object({
		productVersion: string().regex(/^[0-9]+(?:\.[0-9]+){1,3}$/u).max(64).optional(),
		buildVersion: string().regex(/^[A-Za-z0-9.]+$/u).max(64).optional(),
		platform: string().trim().min(1).max(PREPARE_STEP_DIAGNOSTICS_OS_PLATFORM_MAX_CHARS),
		release: string().trim().max(PREPARE_STEP_DIAGNOSTICS_OS_RELEASE_MAX_CHARS)
	}),
	inventoryAnomalies: union([array(PREPARE_STEP_DIAGNOSTICS_SHORT_TEXT_SCHEMA).max(32), nullFactory()]),
	serviceJob: union([prepareStepDiagnosticsServiceJobSchema, nullFactory()]),
	lifecycleJournalTail: union([array(prepareStepDiagnosticsLifecycleJournalEntrySchema).max(10), nullFactory()]),
	serviceLogTail: union([array(string().trim().max(PREPARE_STEP_DIAGNOSTICS_LOG_LINE_MAX_CHARS)).max(30), nullFactory()]),
	serviceLogNote: enumFactory(["file_missing", "file_empty"]).optional(),
	collectionErrors: array(string().trim().min(1).max(PREPARE_STEP_DIAGNOSTICS_COLLECTION_ERROR_MAX_CHARS)).max(8),
	truncated: boolean()
});
const PREPARE_STEP_DIAGNOSTICS_REDACTION_REPLACEMENTS = [
	[/\b(authorization\s*:\s*(?:bearer|basic)\s+)[^\s,;]+/gi, "$1[redacted]"],
	[/\b(x-openmeld-prepare-token\s*[:=]\s*["']?)[^"',;\s]+/gi, "$1[redacted]"],
	[/\b((?:set-cookie|cookie)\s*:\s*)[^\r\n]+/gi, "$1[redacted]"],
	[/\bopenMeldDaemon_[A-Za-z0-9._~:/+=-]+/g, "[redacted]"],
	[/\b[A-Fa-f0-9]{40,}\b/g, "[redacted]"],
	[/\b[A-Za-z0-9+/]{40,}={0,2}/g, "[redacted]"]
];
function redactPrepareStepDiagnosticsText(value) {
	let redacted = value;
	for (const [pattern, replacement] of PREPARE_STEP_DIAGNOSTICS_REDACTION_REPLACEMENTS) redacted = redacted.replace(pattern, replacement);
	return redacted;
}
function redactPrepareStepDiagnosticsValue(value) {
	if (typeof value === "string") return redactPrepareStepDiagnosticsText(value);
	if (Array.isArray(value)) return value.map((entry) => redactPrepareStepDiagnosticsValue(entry));
	if (value && typeof value === "object") return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, redactPrepareStepDiagnosticsValue(entry)]));
	return value;
}
function redactPrepareStepDiagnosticsBundle(bundle) {
	return redactPrepareStepDiagnosticsValue(bundle);
}
const PREPARE_URL_SCHEMA = string().trim().min(1).max(2048).refine((value) => {
	try {
		new URL(value);
		return true;
	} catch {
		return false;
	}
}, { message: "expected a valid URL" });
const PREPARE_SESSION_STREAM_PROTOCOL_VERSION = 1;
const prepareOpaqueTokenSchema = string().trim().min(1).max(2048);
const prepareSessionLifecycleStatusSchema = enumFactory([
	"active",
	"completed",
	"failed",
	"cancelled",
	"expired"
]);
const prepareSessionPhaseSchema = enumFactory([
	"waiting_for_terminal",
	"terminal_connected",
	"checking_computer",
	"waiting_for_agent_selection",
	"preparing_local_agents",
	"starting_service",
	"reporting_runtime",
	"verifying_readiness",
	"ready",
	"blocked",
	"terminal_disconnected",
	"failed",
	"expired"
]);
const prepareStepIdSchema = enumFactory([
	"computer",
	"connect_terminal",
	"choose_local_agents",
	"prepare_selected_agents",
	"refresh_service",
	"report_runtime",
	"verify_setup",
	"ready"
]);
const prepareStepStateSchema = enumFactory([
	"pending",
	"active",
	"waiting_for_user",
	"complete",
	"blocked",
	"terminal_disconnected"
]);
const prepareStepSchema = strictObject({
	id: prepareStepIdSchema,
	state: prepareStepStateSchema,
	title: PREPARE_USER_MESSAGE_SCHEMA,
	summary: union([PREPARE_USER_MESSAGE_SCHEMA, nullFactory()]),
	blockerReasonCode: union([PREPARE_REASON_CODE_SCHEMA, nullFactory()]),
	startedAt: PREPARE_NULLABLE_DATE_TIME_SCHEMA,
	completedAt: PREPARE_NULLABLE_DATE_TIME_SCHEMA,
	updatedAt: PREPARE_ISO_DATE_TIME_SCHEMA
}).superRefine((value, ctx) => {
	if (value.state === "blocked" && value.blockerReasonCode === null) ctx.addIssue({
		code: "custom",
		message: "blocked prepare steps must include blockerReasonCode",
		path: ["blockerReasonCode"]
	});
	if (value.state !== "blocked" && value.blockerReasonCode !== null) ctx.addIssue({
		code: "custom",
		message: "non-blocked prepare steps must use a null blockerReasonCode",
		path: ["blockerReasonCode"]
	});
	if (value.state === "complete" && value.completedAt === null) ctx.addIssue({
		code: "custom",
		message: "complete prepare steps must include completedAt",
		path: ["completedAt"]
	});
});
const prepareTerminalConnectionStatusSchema = enumFactory([
	"never_connected",
	"connected",
	"disconnected",
	"replaced"
]);
const prepareTerminalConnectionSchema = strictObject({
	status: prepareTerminalConnectionStatusSchema,
	connectionId: union([PREPARE_BOUNDED_ID_SCHEMA, nullFactory()]),
	connectionEpoch: union([int().positive(), nullFactory()]),
	connectedAt: PREPARE_NULLABLE_DATE_TIME_SCHEMA,
	lastSeenAt: PREPARE_NULLABLE_DATE_TIME_SCHEMA,
	disconnectedAt: PREPARE_NULLABLE_DATE_TIME_SCHEMA,
	disconnectReason: union([PREPARE_REASON_CODE_SCHEMA, nullFactory()])
}).superRefine((value, ctx) => {
	if (value.status === "connected") {
		if (value.connectionId === null) ctx.addIssue({
			code: "custom",
			message: "connected terminal must include connectionId",
			path: ["connectionId"]
		});
		if (value.connectionEpoch === null) ctx.addIssue({
			code: "custom",
			message: "connected terminal must include connectionEpoch",
			path: ["connectionEpoch"]
		});
		if (value.connectedAt === null) ctx.addIssue({
			code: "custom",
			message: "connected terminal must include connectedAt",
			path: ["connectedAt"]
		});
	}
});
const prepareBlockerSeveritySchema = enumFactory(["user_action_required", "system_blocked"]);
const prepareBlockerRetryActionSchema = enumFactory([
	"run_prepare_command",
	"copy_fresh_command",
	"choose_agents",
	"refresh",
	"contact_support"
]);
const prepareBlockerSchema = strictObject({
	blockerId: PREPARE_BOUNDED_ID_SCHEMA,
	stepId: prepareStepIdSchema,
	reasonCode: PREPARE_REASON_CODE_SCHEMA,
	message: PREPARE_USER_MESSAGE_SCHEMA,
	severity: prepareBlockerSeveritySchema,
	retryAction: prepareBlockerRetryActionSchema,
	createdAt: PREPARE_ISO_DATE_TIME_SCHEMA
});
const prepareLocalAgentIdsSchema = array(PREPARE_BOUNDED_ID_SCHEMA).superRefine((value, ctx) => {
	if (new Set(value).size !== value.length) ctx.addIssue({
		code: "custom",
		message: "localAgentIds must not contain duplicates"
	});
});
const prepareLocalAgentCatalogSchema = localAgentCatalogSchema;
const prepareLocalReadinessSnapshotSchema = strictObject({
	observedAt: PREPARE_ISO_DATE_TIME_SCHEMA,
	service: startBackgroundHelperProjectionSchema,
	skills: startSkillsReadinessProjectionSchema
});
const prepareSetupBootstrapStepSchema = enumFactory([
	"human_profile",
	"assistant_profile",
	"account_space_index",
	"space_create",
	"space_membership",
	"binding",
	"welcome_message",
	"unknown"
]);
const prepareSetupBootstrapProfileSummarySchema = strictObject({
	openMeldProfileId: PREPARE_BOUNDED_ID_SCHEMA,
	profileKind: authProfileKindSchema,
	profileName: PREPARE_USER_MESSAGE_SCHEMA
});
const prepareSetupBootstrapFactsSchema = strictObject({
	ownerUserId: PREPARE_BOUNDED_ID_SCHEMA,
	assistantProfileId: PREPARE_BOUNDED_ID_SCHEMA,
	spaceId: PREPARE_BOUNDED_ID_SCHEMA,
	spaceUrl: PREPARE_URL_SCHEMA,
	createdAt: PREPARE_ISO_DATE_TIME_SCHEMA
});
const legacyPrepareSetupBootstrapAssistantProfileSchema = prepareSetupBootstrapProfileSummarySchema.extend({
	managedRole: authProfileManagedRoleSchema.extract(["openmeld_assistant"]),
	profileKind: literal("agent")
});
const legacyPrepareSetupBootstrapMembershipSchema = strictObject({
	addedMemberIds: array(PREPARE_BOUNDED_ID_SCHEMA),
	alreadyMemberIds: array(PREPARE_BOUNDED_ID_SCHEMA),
	assistantProfileId: PREPARE_BOUNDED_ID_SCHEMA,
	humanProfileId: PREPARE_BOUNDED_ID_SCHEMA
});
const legacyPrepareSetupBootstrapWelcomingSpaceSchema = strictObject({
	createdAt: PREPARE_ISO_DATE_TIME_SCHEMA,
	name: PREPARE_USER_MESSAGE_SCHEMA,
	outcome: enumFactory(["created", "reused"]).optional(),
	spaceId: PREPARE_BOUNDED_ID_SCHEMA,
	url: PREPARE_URL_SCHEMA
});
const prepareSetupBootstrapResultSchema = discriminatedUnion("status", [strictObject({
	status: literal("completed"),
	assistantProfile: union([legacyPrepareSetupBootstrapAssistantProfileSchema, nullFactory()]),
	binding: union([strictObject({
		agentControllerRef: PREPARE_BOUNDED_ID_SCHEMA,
		openMeldServiceRuntimeId: PREPARE_BOUNDED_ID_SCHEMA,
		outcome: enumFactory([
			"created",
			"reused",
			"reconnected"
		]),
		reason: union([PREPARE_REASON_CODE_SCHEMA, nullFactory()]).optional(),
		reasonCodes: array(PREPARE_REASON_CODE_SCHEMA),
		routable: literal(true),
		source: literal("agent_profile_binding_api"),
		status: literal("accepted")
	}), nullFactory()]),
	bindingWarning: union([strictObject({
		code: PREPARE_REASON_CODE_SCHEMA,
		message: PREPARE_USER_MESSAGE_SCHEMA,
		retryCommand: PREPARE_LONG_TEXT_SCHEMA
	}), nullFactory()]).optional(),
	humanProfile: prepareSetupBootstrapProfileSummarySchema.extend({ profileKind: literal("human") }),
	facts: union([prepareSetupBootstrapFactsSchema, nullFactory()]),
	membership: union([legacyPrepareSetupBootstrapMembershipSchema, nullFactory()]),
	retryCommand: nullFactory(),
	warnings: array(strictObject({
		code: PREPARE_REASON_CODE_SCHEMA,
		message: PREPARE_USER_MESSAGE_SCHEMA,
		profileId: PREPARE_BOUNDED_ID_SCHEMA
	})),
	welcomeMessage: union([strictObject({
		signalId: PREPARE_BOUNDED_ID_SCHEMA,
		text: PREPARE_LONG_TEXT_SCHEMA
	}), nullFactory()]),
	welcomingSpace: union([legacyPrepareSetupBootstrapWelcomingSpaceSchema, nullFactory()])
}), strictObject({
	status: literal("failed"),
	diagnostics: strictObject({
		errorName: union([PREPARE_BOUNDED_ID_SCHEMA, nullFactory()]),
		selectedAgentIds: prepareLocalAgentIdsSchema
	}),
	failedStep: prepareSetupBootstrapStepSchema,
	message: PREPARE_LONG_TEXT_SCHEMA,
	reason: literal("bootstrap_failed"),
	retryCommand: PREPARE_LONG_TEXT_SCHEMA
})]);
const prepareSetupBootstrapWriteSchema = strictObject({
	prepareSessionId: PREPARE_BOUNDED_ID_SCHEMA,
	setupBootstrap: prepareSetupBootstrapResultSchema
});
const prepareSessionSnapshotSchema = strictObject({
	prepareSessionId: PREPARE_BOUNDED_ID_SCHEMA,
	ownerUserId: PREPARE_BOUNDED_ID_SCHEMA,
	connectedComputerId: union([PREPARE_BOUNDED_ID_SCHEMA, nullFactory()]).optional(),
	humanProfileId: union([openMeldProfileIdSchema, nullFactory()]),
	deviceId: union([PREPARE_BOUNDED_ID_SCHEMA, nullFactory()]),
	runtimeLane: union([PREPARE_BOUNDED_ID_SCHEMA, nullFactory()]),
	targetSpaceId: union([PREPARE_BOUNDED_ID_SCHEMA, nullFactory()]),
	sessionRevision: int().nonnegative(),
	lifecycleStatus: prepareSessionLifecycleStatusSchema,
	phase: prepareSessionPhaseSchema,
	steps: array(prepareStepSchema),
	localAgentCatalog: union([prepareLocalAgentCatalogSchema, nullFactory()]).default(null),
	localReadinessSnapshot: union([prepareLocalReadinessSnapshotSchema, nullFactory()]).default(null),
	setupBootstrap: prepareSetupBootstrapResultSchema.optional(),
	selectedLocalAgentIds: prepareLocalAgentIdsSchema,
	terminalConnection: prepareTerminalConnectionSchema,
	blockers: array(prepareBlockerSchema),
	diagnosticCorrelationId: PREPARE_BOUNDED_ID_SCHEMA,
	createdAt: PREPARE_ISO_DATE_TIME_SCHEMA,
	updatedAt: PREPARE_ISO_DATE_TIME_SCHEMA,
	completedAt: PREPARE_NULLABLE_DATE_TIME_SCHEMA,
	expiresAt: PREPARE_ISO_DATE_TIME_SCHEMA
}).superRefine((value, ctx) => {
	const terminalLifecycle = value.lifecycleStatus === "completed" || value.lifecycleStatus === "failed" || value.lifecycleStatus === "cancelled" || value.lifecycleStatus === "expired";
	if (terminalLifecycle && value.completedAt === null) ctx.addIssue({
		code: "custom",
		message: "terminal prepare sessions must include completedAt",
		path: ["completedAt"]
	});
	if (!terminalLifecycle && value.completedAt !== null) ctx.addIssue({
		code: "custom",
		message: "active prepare sessions must use a null completedAt",
		path: ["completedAt"]
	});
	if (value.phase === "blocked" && value.blockers.length === 0) ctx.addIssue({
		code: "custom",
		message: "blocked prepare sessions must include at least one blocker",
		path: ["blockers"]
	});
});
const prepareSessionEventTypeSchema = enumFactory([
	"session.created",
	"session.snapshot_updated",
	"session.completed",
	"session.failed",
	"session.cancelled",
	"session.expired",
	"token.minted",
	"token.revoked",
	"terminal.connected",
	"terminal.disconnected",
	"terminal.connection_replaced",
	"terminal.local_agent_catalog_reported",
	"terminal.agent_selection_saved",
	"terminal.setup_bootstrap_recorded",
	"terminal.step_started",
	"terminal.step_completed",
	"terminal.step_failed",
	"web.agent_selection_saved",
	"blocker.added",
	"blocker.cleared"
]);
const prepareSessionEventActorSchema = enumFactory([
	"web",
	"terminal",
	"system"
]);
const prepareSessionEventSchema = strictObject({
	eventId: PREPARE_BOUNDED_ID_SCHEMA,
	prepareSessionId: PREPARE_BOUNDED_ID_SCHEMA,
	sessionRevision: int().nonnegative(),
	eventType: prepareSessionEventTypeSchema,
	actor: prepareSessionEventActorSchema,
	stepId: union([prepareStepIdSchema, nullFactory()]),
	connectionEpoch: union([int().positive(), nullFactory()]),
	reasonCode: union([PREPARE_REASON_CODE_SCHEMA, nullFactory()]),
	message: union([PREPARE_USER_MESSAGE_SCHEMA, nullFactory()]),
	localAgentIds: prepareLocalAgentIdsSchema,
	createdAt: PREPARE_ISO_DATE_TIME_SCHEMA
});
const createPrepareSessionRequestSchema = strictObject({
	connectedComputerId: union([PREPARE_BOUNDED_ID_SCHEMA, nullFactory()]).optional(),
	humanProfileId: union([openMeldProfileIdSchema, nullFactory()]).optional(),
	deviceId: union([PREPARE_BOUNDED_ID_SCHEMA, nullFactory()]).optional(),
	runtimeLane: union([PREPARE_BOUNDED_ID_SCHEMA, nullFactory()]).optional(),
	targetSpaceId: union([PREPARE_BOUNDED_ID_SCHEMA, nullFactory()]).optional(),
	selectedLocalAgentIds: prepareLocalAgentIdsSchema.optional(),
	sourceNavigationAction: strictObject({
		signalId: PREPARE_BOUNDED_ID_SCHEMA,
		spaceId: PREPARE_BOUNDED_ID_SCHEMA
	}).optional()
});
const prepareSessionWithTokenResponseSchema = strictObject({
	snapshot: prepareSessionSnapshotSchema,
	opaqueToken: prepareOpaqueTokenSchema,
	opaqueTokenExpiresAt: PREPARE_ISO_DATE_TIME_SCHEMA
});
const createPrepareSessionResponseSchema = prepareSessionWithTokenResponseSchema;
const prepareSessionResponseSchema = strictObject({ snapshot: prepareSessionSnapshotSchema });
const terminalStepWriteStatusSchema = enumFactory([
	"started",
	"completed",
	"failed"
]);
const terminalStepWriteSchema = strictObject({
	prepareSessionId: PREPARE_BOUNDED_ID_SCHEMA,
	connectionEpoch: int().positive(),
	expectedRevision: int().nonnegative(),
	deviceId: union([PREPARE_BOUNDED_ID_SCHEMA, nullFactory()]).optional(),
	runtimeLane: union([PREPARE_BOUNDED_ID_SCHEMA, nullFactory()]).optional(),
	stepAttemptId: PREPARE_BOUNDED_ID_SCHEMA,
	stepId: prepareStepIdSchema,
	status: terminalStepWriteStatusSchema,
	localReadinessSnapshot: union([prepareLocalReadinessSnapshotSchema, nullFactory()]).optional(),
	reasonCode: union([PREPARE_REASON_CODE_SCHEMA, nullFactory()]).optional(),
	message: union([PREPARE_USER_MESSAGE_SCHEMA, nullFactory()]).optional(),
	diagnostics: PREPARE_STEP_DIAGNOSTICS_SCHEMA.optional()
});
const terminalLocalAgentCatalogWriteSchema = strictObject({
	prepareSessionId: PREPARE_BOUNDED_ID_SCHEMA,
	connectionEpoch: int().positive(),
	localAgentCatalog: prepareLocalAgentCatalogSchema
});
const terminalAgentSelectionWriteSchema = strictObject({
	prepareSessionId: PREPARE_BOUNDED_ID_SCHEMA,
	connectionEpoch: int().positive(),
	expectedRevision: int().nonnegative(),
	localAgentIds: prepareLocalAgentIdsSchema
});
const webAgentSelectionWriteSchema = strictObject({
	prepareSessionId: PREPARE_BOUNDED_ID_SCHEMA,
	expectedRevision: int().nonnegative(),
	localAgentIds: prepareLocalAgentIdsSchema
});
const prepareDiagnosticResponseSchema = strictObject({
	snapshot: prepareSessionSnapshotSchema,
	events: array(prepareSessionEventSchema)
});
const prepareSessionStreamRoleSchema = enumFactory(["web", "terminal"]);
const prepareConnectionProgressWriteSchema = computerConnectionProgressObservationSchema.extend({
	connectionEpoch: int().positive(),
	prepareSessionId: PREPARE_BOUNDED_ID_SCHEMA
});
const prepareSessionStreamClientFrameSchema = discriminatedUnion("type", [
	strictObject({
		type: literal("client.ping"),
		payload: strictObject({ sentAt: PREPARE_ISO_DATE_TIME_SCHEMA })
	}),
	strictObject({
		type: literal("client.hello"),
		payload: strictObject({
			cliVersion: PREPARE_BOUNDED_ID_SCHEMA,
			protocolVersion: int().positive()
		})
	}),
	strictObject({
		type: literal("terminal.step.write"),
		payload: terminalStepWriteSchema
	}),
	strictObject({
		type: literal("terminal.connection_progress.write"),
		payload: prepareConnectionProgressWriteSchema
	}),
	strictObject({
		type: literal("terminal.local_agent_catalog.write"),
		payload: terminalLocalAgentCatalogWriteSchema
	}),
	strictObject({
		type: literal("terminal.agent_selection.write"),
		payload: terminalAgentSelectionWriteSchema
	}),
	strictObject({
		type: literal("web.agent_selection.write"),
		payload: webAgentSelectionWriteSchema
	})
]);
const prepareSessionStreamServerFrameSchema = discriminatedUnion("type", [
	strictObject({
		type: literal("server.pong"),
		payload: strictObject({ sentAt: PREPARE_ISO_DATE_TIME_SCHEMA })
	}),
	strictObject({
		type: literal("server.capabilities"),
		payload: strictObject({ connectionProgress: literal(1) })
	}),
	strictObject({
		type: literal("snapshot"),
		payload: strictObject({ snapshot: prepareSessionSnapshotSchema })
	}),
	strictObject({
		type: literal("snapshot_changed"),
		payload: strictObject({ snapshot: prepareSessionSnapshotSchema })
	}),
	strictObject({
		type: literal("connection_progress"),
		payload: prepareConnectionProgressWriteSchema
	}),
	strictObject({
		type: literal("terminal.replaced"),
		payload: strictObject({
			reasonCode: PREPARE_REASON_CODE_SCHEMA,
			message: PREPARE_USER_MESSAGE_SCHEMA
		})
	}),
	strictObject({
		type: literal("server.error"),
		payload: strictObject({
			code: PREPARE_REASON_CODE_SCHEMA,
			message: PREPARE_USER_MESSAGE_SCHEMA
		})
	})
]);
//#endregion
export { PREPARE_SESSION_STREAM_PROTOCOL_VERSION, PREPARE_STEP_DIAGNOSTICS_MAX_CHARS, createPrepareSessionRequestSchema, createPrepareSessionResponseSchema, prepareBlockerRetryActionSchema, prepareBlockerSchema, prepareBlockerSeveritySchema, prepareConnectionProgressWriteSchema, prepareDiagnosticResponseSchema, prepareLocalAgentCatalogSchema, prepareLocalAgentIdsSchema, prepareLocalReadinessSnapshotSchema, prepareOpaqueTokenSchema, prepareSessionContinuationSchema, prepareSessionEventActorSchema, prepareSessionEventSchema, prepareSessionEventTypeSchema, prepareSessionLifecycleStatusSchema, prepareSessionPhaseSchema, prepareSessionResponseSchema, prepareSessionSnapshotSchema, prepareSessionStreamClientFrameSchema, prepareSessionStreamRoleSchema, prepareSessionStreamServerFrameSchema, prepareSessionWithTokenResponseSchema, prepareSetupBootstrapFactsSchema, prepareSetupBootstrapProfileSummarySchema, prepareSetupBootstrapResultSchema, prepareSetupBootstrapStepSchema, prepareSetupBootstrapWriteSchema, prepareStepDiagnosticsBundleSchema, prepareStepDiagnosticsLifecycleEventTypeSchema, prepareStepDiagnosticsLifecycleJournalEntrySchema, prepareStepDiagnosticsServiceJobSchema, prepareStepIdSchema, prepareStepSchema, prepareStepStateSchema, prepareTerminalConnectionSchema, prepareTerminalConnectionStatusSchema, redactPrepareStepDiagnosticsBundle, redactPrepareStepDiagnosticsText, redactPrepareStepDiagnosticsValue, terminalAgentSelectionWriteSchema, terminalLocalAgentCatalogWriteSchema, terminalStepWriteSchema, terminalStepWriteStatusSchema, webAgentSelectionWriteSchema };

//# sourceMappingURL=session.js.map